export interface AutocompletePrediction {
    description: string;
    place_id: string;
    reference?: string;
    types?: string[];
    structured_formatting?: {
        main_text: string;
        main_text_matched_substrings?: Array<{ length: number; offset: number }>;
        secondary_text: string;
    };
    terms?: Array<{ offset: number; value: string }>;
    matched_substrings?: Array<{ length: number; offset: number }>;
    // Added when place details are fetched
    geometry?: {
        location: {
            lat: number;
            lng: number;
        };
        viewport?: {
            northeast: { lat: number; lng: number };
            southwest: { lat: number; lng: number };
        };
    };
    name?: string;
    formatted_address?: string;
    business_status?: string;
    icon?: string;
    vicinity?: string;
    distance_meters?: number;
}

export async function fetchAutocompleteSuggestions(
    input: string,
    location?: { lat: number; lng: number },
    radius?: number,
    types?: string, // e.g., 'establishment', 'geocode', or '(regions)|(cities)'
    strictbounds?: boolean,
    language?: string
): Promise<AutocompletePrediction[]> {
    if (!input) return [];

    let endpoint = 'https://places-api-manager-613221148836.europe-west1.run.app/placesreq?action=autocomplete&input=' + encodeURIComponent(input);

    if (language) {
        endpoint += `&language=${language}`;
    }

    // Add types parameter to search for establishments (businesses) by default
    if (types) {
        endpoint += `&types=${types}`;
    }

    if (location) {
        endpoint += `&location=${location.lat},${location.lng}`;
        // origin enables distance_meters in response for sorting by proximity
        endpoint += `&origin=${location.lat},${location.lng}`;
        if (radius) endpoint += `&radius=${radius}`;
        if (strictbounds) endpoint += `&strictbounds=true`;
    }

    try {
        const response = await fetch(endpoint);
        const data = await response.json();

        // If we get REQUEST_DENIED, log more details
        if (data.status === 'REQUEST_DENIED') {
            console.error('❌ Google Places API Request Denied');
            console.error('Error message:', data.error_message);
            console.error('This usually means:');
            console.error('1. Invalid API key');
            console.error('2. Places API not enabled');
            console.error('3. API key restrictions blocking the request');
        }

        const predictions: AutocompletePrediction[] = data.predictions || [];

        // Sort by distance if available (closest first)
        if (predictions.some(p => p.distance_meters != null)) {
            predictions.sort((a, b) => (a.distance_meters ?? Infinity) - (b.distance_meters ?? Infinity));
        }

        return predictions;
    } catch (error) {
        console.error('Autocomplete fetch error:', error);
        return [];
    }
}